import { useState } from "react"
import { format } from "date-fns"

import { DISPLAY_MODES, MAX_BODY_BYTES } from "@constants"

const _startOfToday = () => {
    const date = new Date()
    date.setHours(0, 0, 0, 0)
    return date
}

export const emptyForm = () => ({
    title: "",
    body: "",
    fileName: "",
    displayMode: DISPLAY_MODES.ONCE,
    audienceCustomers: [],
    audienceUsers: [],
    startAt: _startOfToday(),
    endAt: null,
    active: true,
})

const _fromRow = row => ({
    title: row.title ?? "",
    body: row.body ?? "",
    fileName: "",
    displayMode: row.displayMode ?? DISPLAY_MODES.ONCE,
    // null means "everyone" in the API; the pickers show that as no selection.
    audienceCustomers: row.audienceCustomers ?? [],
    audienceUsers: row.audienceUsers ?? [],
    startAt: row.startAt ? new Date(row.startAt) : _startOfToday(),
    endAt: row.endAt ? new Date(row.endAt) : null,
    active: !!row.active,
})

/** Local wall-clock string, deliberately without a timezone suffix.
 *  `toISOString()` would shift a 00:00 Madrid start to 22:00 the day before in UTC, and the
 *  backend compares these against MySQL's NOW(). Sending the local time keeps "starts on the
 *  1st" meaning the 1st. */
const _toApiDate = date => (date ? format(date, "yyyy-MM-dd'T'HH:mm:ss") : null)

/** The date picker hands back midnight. Left as is, "ends on the 30th" would stop showing
 *  the announcement as the 30th began — a day short of what the user asked for. */
const _endOfDay = date => {
    if (!date) return null
    const end = new Date(date)
    end.setHours(23, 59, 59, 0)
    return end
}

export const toPayload = form => ({
    title: form.title.trim(),
    body: form.body,
    displayMode: form.displayMode,
    // An empty picker means everyone, which the API spells as null. It rejects [].
    audienceCustomers: form.audienceCustomers.length ? form.audienceCustomers : null,
    audienceUsers: form.audienceUsers.length ? form.audienceUsers : null,
    startAt: _toApiDate(form.startAt),
    endAt: _toApiDate(_endOfDay(form.endAt)),
    active: form.active,
})

export function validate(form) {
    const errors = {}
    if (!form.title.trim()) errors.title = "Required"
    if (form.title.length > 200) errors.title = "Up to 200 characters"
    if (!form.body.trim()) errors.body = "Upload an .html file or paste the HTML"
    // Bytes, not characters: the backend measures the UTF-8 length, so accented text counts
    // for more than it looks.
    if (new Blob([form.body]).size > MAX_BODY_BYTES)
        errors.body = "Over 256 KB. Link images by URL instead of embedding them."
    if (!form.startAt) errors.startAt = "Required"
    if (form.endAt && form.startAt && form.endAt <= form.startAt)
        errors.endAt = "Must be later than the start date"
    return errors
}

/** Plain React state rather than a signal: this is local state for one modal, nothing
 *  outside it reads the form, and useState is the plainer tool for that. */
export default function useAnnouncementForm() {
    const [form, setForm] = useState(emptyForm())
    const [errors, setErrors] = useState({})

    const load = row => {
        setForm(row ? _fromRow(row) : emptyForm())
        setErrors({})
    }

    const setField = (field, value) => {
        setForm(previous => ({ ...previous, [field]: value }))
        setErrors(previous => {
            if (!previous[field]) return previous
            const { [field]: _dropped, ...rest } = previous
            return rest
        })
    }

    /** Read the uploaded file in the browser and send its text as a plain string. The API
     *  takes JSON, not multipart. */
    const loadFile = file => new Promise((resolve, reject) => {
        if (!file) return resolve()
        if (file.size > MAX_BODY_BYTES) {
            setErrors(previous => ({ ...previous, body: "File is over 256 KB" }))
            return reject(new Error("file too large"))
        }
        const reader = new FileReader()
        reader.onload = () => {
            setForm(previous => ({
                ...previous,
                body: String(reader.result ?? ""),
                fileName: file.name,
            }))
            setErrors(previous => {
                const { body: _dropped, ...rest } = previous
                return rest
            })
            resolve()
        }
        reader.onerror = () => {
            setErrors(previous => ({ ...previous, body: "Could not read the file" }))
            reject(reader.error)
        }
        reader.readAsText(file)
    })

    const isValid = () => {
        const found = validate(form)
        setErrors(found)
        return Object.keys(found).length === 0
    }

    return { form, errors, load, setField, loadFile, isValid }
}
